
import { computed, onMounted, ref, watch } from "vue";
import { showToastError, showToastSuccess } from "@/helpers/notification";
import { i18n } from "@/lang";
import { useStore } from "vuex";
import _ from "lodash";
import { RequestedAction } from "@moderrotech/servers-communication-library/src/types";
import ImageEditor from "@/components/ImageEditor.vue";

export default {
  components: { ImageEditor },
  setup() {
    const store = useStore();

    const image = ref<string>();

    onMounted(async () => {
      store.commit(
        "session/UNSET_REQUESTED_ACTION",
        RequestedAction.hideDrawingPad
      );

      const { url, opacity } =
        _.find(store.state.session.requestedActions, {
          action: RequestedAction.showDrawingPad
        })?.payload?.background ?? {};

      if (!opacity || opacity === 1) {
        image.value = url;
        return;
      }

      image.value = (await (async () =>
        new Promise((resolve, reject) => {
          const tmpImg = new Image();
          tmpImg.crossOrigin = "anonymous";

          tmpImg.addEventListener("load", () => {
            const canvas = document.createElement("canvas");
            canvas.width = tmpImg.width;
            canvas.height = tmpImg.height;
            const ctx = canvas.getContext("2d");
            ctx!.globalAlpha = opacity;
            ctx!.drawImage(tmpImg, 0, 0);

            try {
              resolve(canvas.toDataURL());
            } catch (err) {
              reject(err);
            }
          });
          tmpImg.src = url;
        }))()) as string;
    });

    const isSubmitting = ref(false);

    const close = () => {
      _.forEach(
        [RequestedAction.hideDrawingPad, RequestedAction.showDrawingPad],
        a => store.commit("session/UNSET_REQUESTED_ACTION", a)
      );
    };

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.hideDrawingPad
        ),
      v => {
        if (v) {
          close();
        }
      }
    );

    const handleSubmitClick = () => {
      isSubmitting.value = true;
      store.dispatch("sharing/shareDocument", {
        file: image.value,
        type: "drawing",
        callback: (data: unknown) => {
          isSubmitting.value = false;
          close();
          showToastSuccess(i18n.global.t("call.success.paintover"));
        },
        errorCallback: (err: unknown) => {
          isSubmitting.value = false;
          showToastError(i18n.global.t("call.error.paintover"));
        }
      });
    };

    return {
      handleSubmitClick,
      handleCancelClick: () => close(),
      isSubmitting,
      image
    };
  }
};
